/** @jsx jsx */
import type { ComponentType, HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { Flex } from '@balance-web/flex';
import { buildDataAttributes, jsx } from '@balance-web/core';
import type { IconProps } from '@balance-web/icon';
import { ChevronDownIcon, ChevronUpIcon, XCircleIcon } from '@balance-web/icon';
import { FOCUS_RING, useTheme } from '@balance-web/theme';
import type { WithDataAttributeProp } from '@balance-web/core';
import { NotificationBadge } from '@balance-web/badge';
import { Inline } from '@balance-web/inline';

import { usePreventableClickHandler } from './utils';
import { useChipVariantStyles } from './shared-styles';
import { InteractionOverlay } from './InteractionOverlay';
import { buttonResetStyles } from './shared-styles';

export type MenuChipProps = WithDataAttributeProp<{
  label: string;
  hasValue: boolean;
  'aria-expanded': boolean;
  variant?: 'filled' | 'outline';
  iconBefore?: ComponentType<IconProps>;
  iconAfter?: ComponentType<IconProps>;
  size?: 'small' | 'medium';
  disabled?: boolean;
  onClear?: () => void;
  selectionCount?: number;
}> &
  HTMLAttributes<HTMLButtonElement>;

export const MenuChip = forwardRef<HTMLButtonElement, MenuChipProps>(
  (
    {
      variant = 'filled',
      size = 'medium',
      label,
      hasValue,
      disabled = false,
      iconBefore: IconBefore,
      iconAfter: IconAfter,
      onClear,
      selectionCount,
      data,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();

    const dataAttributes = buildDataAttributes(data);
    const handleClick = usePreventableClickHandler(props, disabled);

    const ChevronIcon = props['aria-expanded']
      ? ChevronUpIcon
      : ChevronDownIcon;
    const hasClear = hasValue && !!onClear;
    const iconColor = disabled
      ? theme.semantic.colour.text.textDisabled
      : theme.semantic.colour.neutral.container.foreground;

    const variantStyles = useChipVariantStyles({
      variant,
      size,
      active: hasValue,
      disabled,
    });

    return (
      <div
        style={{
          position: 'relative',
          display: 'inline-flex',
        }}
      >
        <button
          ref={ref}
          aria-disabled={disabled}
          // Menu triggers can receive roles like "combobox" that don't compute
          // an accessible name from content, so name the trigger explicitly.
          aria-label={label}
          css={{
            /** Reset button styles start */
            ...buttonResetStyles,
            /** Reset button styles end */

            /** Immutable styles start */
            position: 'relative',
            fontWeight: theme.typography.fontWeight.semibold,
            borderRadius: theme.components.chip.borderRadius,

            '&:not([aria-disabled=true]):focus-visible': {
              boxShadow: `0 0 0 2px ${FOCUS_RING}`,
            },
            /** Immutable styles end */

            /** Variant styles start */
            ...variantStyles,
            ...(variant === 'filled'
              ? {
                  backgroundColor: disabled
                    ? theme.semantic.colour.neutral.inverse.interactions.hover
                    : theme.semantic.colour.surface.neutral.background1,
                  boxShadow: `inset 0 0 0 1px ${
                    disabled
                      ? theme.semantic.colour.neutral.inverse.interactions.hover
                      : theme.semantic.colour.neutral.border
                  }`,
                  color: disabled
                    ? theme.semantic.colour.text.textDisabled
                    : theme.semantic.colour.neutral.container.foreground,
                  '&[aria-disabled=true]': {
                    cursor: 'default',
                    color: theme.semantic.colour.text.textDisabled,
                    backgroundColor:
                      theme.semantic.colour.neutral.inverse.interactions.hover,
                  },
                  '&:not([aria-disabled=true])': {
                    ':hover, &[aria-expanded=true]': {
                      ' .interactionOverlay': {
                        backgroundColor:
                          theme.semantic.colour.neutral.container.interactions
                            .hover,
                      },
                    },
                    ':active': {
                      ' .interactionOverlay': {
                        backgroundColor:
                          theme.semantic.colour.neutral.container.interactions
                            .pressed,
                      },
                    },
                  },
                }
              : {}),
            /** Variant styles end */

            paddingRight: hasClear
              ? `calc(${theme.spacing.large} * 3 + ${theme.spacing.small})`
              : `calc(${theme.spacing.large} * 2)`,
          }}
          type="button"
          {...dataAttributes}
          {...props}
          onClick={handleClick}
        >
          <InteractionOverlay />
          <Flex
            alignItems="center"
            gap="small"
            height="100%"
            justifyContent="center"
          >
            {IconBefore ? <IconBefore size="small" /> : null}
            <span>{label}</span>
            {selectionCount !== undefined && selectionCount > 0 ? (
              <NotificationBadge
                tone={variant === 'outline' ? 'primary' : 'neutral'}
                value={selectionCount}
              />
            ) : null}
            {IconAfter ? <IconAfter size="small" /> : null}
          </Flex>
        </button>
        <Inline
          alignY="center"
          gap="xsmall"
          css={{
            pointerEvents: 'none',
          }}
          height="100%"
          position="absolute"
          right={variantStyles.paddingInline}
        >
          {hasClear ? (
            <button
              aria-disabled={disabled}
              css={{
                ...buttonResetStyles,
                pointerEvents: 'auto',
                '&:not([aria-disabled=true]):focus-visible, &:not([aria-disabled=true]):hover': {
                  boxShadow: `0 0 0 2px ${FOCUS_RING}`,
                },
              }}
              disabled={disabled}
              type="button"
              onClick={onClear}
            >
              <XCircleIcon
                color="textLink"
                css={{
                  stroke: iconColor,
                }}
                size="small"
              />
            </button>
          ) : null}
          <ChevronIcon
            color="textLink"
            css={{
              stroke: iconColor,
            }}
            size="small"
          />
        </Inline>
      </div>
    );
  }
);
